import { useEffect, useState } from "react";
import api from "../lib/api";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { ShieldCheck, Warning, Equals } from "@phosphor-icons/react";

export default function Audit() {
    const [els, setEls] = useState([]);
    const [audits, setAudits] = useState({}); // {electionId: auditPayload}
    const [chainStatus, setChainStatus] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [e, c] = await Promise.all([api.get("/elections"), api.get("/blockchain/verify")]);
                setEls(e.data);
                setChainStatus(c.data);
                // Fetch audit for every published election in parallel.
                const results = await Promise.all(
                    e.data.map(async (el) => {
                        try { const { data } = await api.get(`/elections/${el.id}/audit`); return [el.id, data]; }
                        catch { return [el.id, null]; }
                    })
                );
                setAudits(Object.fromEntries(results));
            } catch {/* noop */}
            setLoading(false);
        })();
    }, []);

    const totalVoters = Object.values(audits).reduce((s, a) => s + (a?.voters_who_voted || 0), 0);
    const totalChain = Object.values(audits).reduce((s, a) => s + (a?.votes_on_chain || 0), 0);
    const allReconciled = els.length > 0 && Object.values(audits).every((a) => a && a.totals_match && a.chain_valid);

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
                <div className="overline text-civic-600 text-[11px]">Public audit</div>
                <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter mt-0.5 text-slate-900 break-words leading-tight">Integrity Verification</h1>
                <p className="text-xs text-slate-600 mt-1 max-w-3xl">
                    The voter roll and the vote chain are stored separately. Their totals must reconcile, and every block hash must verify against the previous.
                </p>

                {/* Summary strip — chain + cross-election totals */}
                <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="audit-summary-strip">
                    <Card
                        className={`p-3 border ${chainStatus?.valid ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
                        data-testid="chain-overall-status"
                    >
                        <div className="flex items-center gap-3">
                            {chainStatus?.valid
                                ? <ShieldCheck size={22} weight="fill" className="text-emerald-600 shrink-0" />
                                : <Warning size={22} weight="fill" className="text-red-600 shrink-0" />}
                            <div className="min-w-0">
                                <div className="overline text-[10px]">Chain integrity</div>
                                <div className="font-heading font-black text-sm mt-0.5 truncate">
                                    {chainStatus?.valid ? "All blocks verified" : `Broken at #${chainStatus?.broken_at}`}
                                </div>
                                <div className="hash-mono text-[10px] text-slate-700">{chainStatus?.length || 0} blocks · SHA-256</div>
                            </div>
                        </div>
                    </Card>
                    <Card className="p-3 border-slate-200 bg-white">
                        <div className="overline text-[10px]">Total voters who voted</div>
                        <div className="font-heading font-black text-2xl tracking-tight text-slate-900 mt-0.5" data-testid="audit-total-voters">{totalVoters}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">Across all elections · roll ledger</div>
                    </Card>
                    <Card className="p-3 border-slate-200 bg-white">
                        <div className="overline text-[10px]">Total votes on chain</div>
                        <div className="font-heading font-black text-2xl tracking-tight text-slate-900 mt-0.5" data-testid="audit-total-chain">{totalChain}</div>
                        <div className={`text-[11px] mt-0.5 flex items-center gap-1 ${allReconciled ? "text-emerald-700" : "text-amber-700"}`}>
                            <Equals size={11} weight="bold" /> {allReconciled ? "All elections reconciled" : "Pending / mismatched"}
                        </div>
                    </Card>
                </div>

                {/* All-elections audit table */}
                <div className="mt-6">
                    <h2 className="font-heading font-bold text-base text-slate-900 mb-2">Reconciliation by election</h2>
                    <Card className="bg-white border-slate-200 overflow-hidden">
                        <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50/70">
                                    <TableHead className="text-[10px] uppercase tracking-wider w-[44%]">Election</TableHead>
                                    <TableHead className="text-[10px] uppercase tracking-wider text-center">Status</TableHead>
                                    <TableHead className="text-[10px] uppercase tracking-wider text-right">Voters who voted</TableHead>
                                    <TableHead className="text-[10px] uppercase tracking-wider text-right">Votes on chain</TableHead>
                                    <TableHead className="text-[10px] uppercase tracking-wider text-center">Reconciled</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody data-testid="audit-table">
                                {loading && (
                                    <TableRow><TableCell colSpan={5} className="text-center text-slate-500 hash-mono text-xs py-6">loading…</TableCell></TableRow>
                                )}
                                {!loading && els.length === 0 && (
                                    <TableRow><TableCell colSpan={5} className="text-center text-slate-500 text-xs py-8">No elections published yet.</TableCell></TableRow>
                                )}
                                {!loading && els.map((e) => {
                                    const a = audits[e.id];
                                    const reconciled = a && a.totals_match && a.chain_valid;
                                    return (
                                        <TableRow key={e.id} className="hover:bg-slate-50" data-testid={`audit-row-${e.id}`}>
                                            <TableCell className="py-2.5">
                                                <div className="font-semibold text-sm text-slate-900 leading-tight">{e.title}</div>
                                                <div className="hash-mono text-[10px] text-slate-500 mt-0.5">#{e.id.slice(0, 8)}</div>
                                            </TableCell>
                                            <TableCell className="py-2.5 text-center">
                                                {e.is_over
                                                    ? <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px]">Closed</Badge>
                                                    : <Badge className="bg-amber-100 text-amber-900 border border-amber-200 text-[10px]">Polls open</Badge>}
                                            </TableCell>
                                            <TableCell className="py-2.5 text-right hash-mono text-base font-semibold text-slate-900" data-testid={`audit-voters-${e.id}`}>
                                                {a ? a.voters_who_voted : "—"}
                                            </TableCell>
                                            <TableCell className="py-2.5 text-right hash-mono text-base font-semibold text-slate-900" data-testid={`audit-chain-${e.id}`}>
                                                {a ? a.votes_on_chain : "—"}
                                            </TableCell>
                                            <TableCell className="py-2.5 text-center">
                                                {!a
                                                    ? <span className="text-slate-400 text-xs">—</span>
                                                    : reconciled
                                                        ? <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-0 text-[10px]">VERIFIED</Badge>
                                                        : <Badge className="bg-red-600 hover:bg-red-600 text-white border-0 text-[10px]">FAILED</Badge>}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                        </div>
                    </Card>
                    <p className="text-[10px] text-slate-500 mt-2 leading-snug">
                        <span className="hash-mono">Roll ledger:</span> identifies <em>who</em> voted, never the choice. <span className="hash-mono">Chain ledger:</span> identifies the <em>choice</em>, never the chooser. The two ledgers must match exactly for the audit to verify.
                    </p>
                </div>
            </div>
        </div>
    );
}
